// Phase 5 drawers (api-phase5.md; prototype drawerHTML 'talk' and 'relay'; blueprint §6 Talk-down, Deterrents):
//   TalkDownDrawer   speakers (list and site map), push-to-talk (hold the button or T), the library by category with an
//                    EN / ES toggle and {zone} filled in, or a typed message; Preview, then Play. What was played on this
//                    event, with its stored audio.
//   DeterrentsDrawer relays with function icons and live state (SSE `device`; countdown for pulses). Pulse (seconds,
//                    default 10, capped at the relay's hardwareMaxOnS), Hold, Release. Two-step confirm: arm, then
//                    confirm within ARM_MS. Gates and locks are marked and always two-step.
//   NotifyDrawer     a notification template to the site's recipients, with the rendered preview; what was sent.
// Device commands carry no version (owner or supervisor); the event refetches on its own SSE `event` message.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, errorText, fetchBlobUrl, type EventAction, type EventDevice, type EventNotification, type NotificationTemplate, type TalkdownMessage, type TalkdownResult } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { SitemapView, type LiveDeviceMark } from '../components/SitemapView.tsx';
import { useLoad, useNow } from '../hooks.ts';
import { useSession } from '../session.tsx';
import { useRefreshOn } from '../stream.tsx';
import { fmtTime, tzAbbr } from '../time.ts';
import type { Ctl } from './ctl.ts';
import { CATEGORY_ORDER, categoryLabel, deviceActive, fillMessage, functionIcon, functionLabel, humanOnly } from './devices.ts';
import { Drawer, Err } from './Drawers.tsx';
import { useDraft } from './drafts.ts';
import { actorCls, actorName, dur, human, plural, str } from './util.ts';

/* ---------- small shared pieces ---------- */

/** Plays an authenticated audio URL (the stored talk-down audio; audited as media access on the server). */
export function AudioButton({ url, label = 'Play audio', small }: { url: string; label?: string; small?: boolean }) {
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  if (src) return <audio className="tdaudio" src={src} controls autoPlay aria-label={label} />;
  return (
    <span className="row-wrap nowrap">
      <button
        type="button"
        className={`btn ${small ? 'sm' : ''}`}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            setSrc(await fetchBlobUrl(url));
          } catch (e) {
            setErr(e);
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? <span className="spinner" aria-hidden="true" /> : <Icon name="play" />}
        {label}
      </button>
      {err ? <span className="note bad-t">{errorText(err)}</span> : null}
    </span>
  );
}

/** Seconds left on a pulse, ticking; null when it has no end. */
function Left({ until }: { until: string | null }) {
  const now = useNow(250);
  const u = until ? Date.parse(until) : NaN;
  if (Number.isNaN(u)) return null;
  return <span className="mono">{dur(Math.max(0, u - now))}</span>;
}

/** Live marks for the site map: the event's speakers and relays with their state. */
export function liveMarks(c: Ctl, now = Date.now()): Map<string, LiveDeviceMark> {
  const human = c.devices.data?.humanOnlyFunctions ?? ['gate', 'lock'];
  return new Map(
    [...c.devices.speakers, ...c.devices.relays].map((d) => [d.id, { kind: d.kind, name: d.name, active: deviceActive(d, now), fn: d.function, humanOnly: humanOnly(d, human) }] as const),
  );
}

/** Talk-downs and relay actions on this event, newest first. */
function deviceActions(c: Ctl, type: 'talk_down' | 'relay'): EventAction[] {
  return c.ev.actions.filter((a) => a.type === type).sort((a, b) => (Date.parse(b.startedAt) || 0) - (Date.parse(a.startedAt) || 0));
}

function speakerNames(c: Ctl, ids: unknown): string {
  const list = Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : [];
  return list.map((id) => c.devices.names.zone(id) ?? id).join(', ') || 'speaker';
}

/** One talk-down in the event's history: who, when, where, what, and its audio. */
export function TalkdownRow({ c, a }: { c: Ctl; a: EventAction }) {
  const tz = c.ev.siteTimeZone;
  const p = a.params;
  const live = p.live === true;
  const name = str(p.messageName);
  const text = str(p.text);
  const r = a.result ?? {};
  const fails = Array.isArray(r.devices) ? (r.devices as { ok?: boolean; error?: string }[]).filter((d) => d.ok === false) : [];
  const simulated = Array.isArray(r.devices) && (r.devices as { simulated?: boolean }[]).some((d) => d.simulated);
  const ms = typeof r.durationMs === 'number' ? r.durationMs : null;
  return (
    <div className={`tdrow ${actorCls(a.actor)}`}>
      <div className="row-wrap">
        <span className="mono dim">{fmtTime(a.startedAt, tz)}</span>
        <b className={`lwho ${actorCls(a.actor)}`}>{a.actor.type === 'agent' ? 'AI' : actorName(a.actor)}</b>
        <span>
          {live ? 'Push-to-talk' : name ? `“${name}”` : 'Typed message'} on {speakerNames(c, p.deviceIds)}
        </span>
        {ms !== null && <span className="mono dim">{(ms / 1000).toFixed(1)} s</span>}
        {simulated && <span className="pill tiny-pill">simulated</span>}
        {a.status !== 'succeeded' && <span className="pill bad tiny-pill">{a.status}</span>}
      </div>
      {text && <div className="tdtext">“{text}”</div>}
      {fails.length > 0 && <div className="note bad-t">Not played on {fails.length}: {fails.map((f) => f.error).filter(Boolean).join('; ')}</div>}
      {a.status === 'succeeded' && <AudioButton url={api.talkdownAudioUrl(a.id)} label="Play what was played" small />}
    </div>
  );
}

/** One relay action in the event's history. */
function RelayRow({ c, a }: { c: Ctl; a: EventAction }) {
  const p = a.params;
  const verb = p.action === 'pulse' ? `pulsed ${typeof p.seconds === 'number' ? `${p.seconds} s` : ''}` : p.action === 'hold' ? 'switched on (hold)' : 'released';
  return (
    <div className={`tdrow ${actorCls(a.actor)}`}>
      <div className="row-wrap">
        <span className="mono dim">{fmtTime(a.startedAt, c.ev.siteTimeZone)}</span>
        <b className={`lwho ${actorCls(a.actor)}`}>{a.actor.type === 'agent' ? 'AI' : actorName(a.actor)}</b>
        <Icon name={functionIcon(str(p.function))} />
        <span>
          {str(p.deviceName) ?? str(p.deviceId) ?? 'Relay'} {verb}
        </span>
        {a.result?.simulated === true && <span className="pill tiny-pill">simulated</span>}
        {a.status !== 'succeeded' && <span className="pill bad tiny-pill">{a.status}</span>}
      </div>
    </div>
  );
}

function DevicesLoading({ c }: { c: Ctl }) {
  if (c.devices.data === undefined && !c.devices.error)
    return (
      <div className="note">
        <span className="spinner" aria-hidden="true" /> Loading the site's devices…
      </div>
    );
  if (c.devices.error)
    return (
      <div className="errbox" role="alert">
        <Icon name="alert" />
        <div className="mono">Could not load the site's devices: {errorText(c.devices.error)}</div>
        <button type="button" className="btn sm" onClick={c.devices.reload}>
          Retry
        </button>
      </div>
    );
  return null;
}

/* ---------- push-to-talk button (prototype .ptt) ---------- */

export function PttButton({ c }: { c: Ctl }) {
  const p = c.ptt;
  const now = useNow(250);
  const down = useRef(false);
  const start = () => {
    if (down.current) return;
    down.current = true;
    c.pttPress();
  };
  const stop = () => {
    if (!down.current) return;
    down.current = false;
    p.release();
  };
  const rec = p.phase === 'recording';
  return (
    <>
      <button
        type="button"
        className={`ptt${rec ? ' on' : ''}${p.phase === 'sending' ? ' sending' : ''}`}
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          start();
        }}
        onPointerUp={stop}
        onPointerCancel={stop}
        onKeyDown={(e) => {
          if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            start();
          }
        }}
        onKeyUp={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            stop();
          }
        }}
        disabled={!c.canAct || !c.speakers.length || p.phase === 'sending'}
        aria-pressed={rec}
        aria-label="Push to talk: hold to record, release to play on the chosen speakers"
      >
        {rec ? (
          <>
            <span className="wave" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <i key={i} style={{ animationDelay: `${i * 0.1}s` }} />
              ))}
            </span>
            REC · {dur(now - (p.startedAt ?? now))} · RELEASE TO SEND
          </>
        ) : p.phase === 'opening' ? (
          <>
            <span className="spinner" aria-hidden="true" /> OPENING THE MICROPHONE…
          </>
        ) : p.phase === 'sending' ? (
          <>
            <span className="spinner" aria-hidden="true" /> PLAYING ON {p.target?.label.toUpperCase() ?? 'THE SPEAKERS'}…
          </>
        ) : (
          <>
            <Icon name="mic" /> HOLD TO TALK · or hold <kbd>T</kbd>
          </>
        )}
      </button>
      {!p.supported && <div className="note warn-t">This browser cannot record the microphone. Use a library or typed message.</div>}
      {p.error && (
        <div className={`warnbox${p.denied ? ' denied' : ''}`} role="alert">
          <b>{p.denied ? 'Microphone blocked' : 'Push-to-talk'}</b>
          <div className="note fg">{p.error}</div>
          <button type="button" className="btn sm self-start" onClick={p.clearError}>
            OK
          </button>
        </div>
      )}
      <div className="note">The clip plays on the speakers when you let go (live streaming comes with the NX gateway). It is stored with the event.</div>
    </>
  );
}

/* ---------- talk-down ---------- */

function usePreview() {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'playing'>('idle');
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => () => audio.current?.pause(), []);
  const stop = () => {
    audio.current?.pause();
    setState('idle');
  };
  const play = async (text: string, language: string) => {
    stop();
    setErr(null);
    setState('loading');
    try {
      const url = await api.talkdownPreview(text, language);
      const a = audio.current ?? new Audio();
      audio.current = a;
      a.src = url;
      a.onended = () => setState('idle');
      await a.play();
      setState('playing');
    } catch (e) {
      setErr(e);
      setState('idle');
    }
  };
  return { state, err, play, stop };
}

type Lang = 'en' | 'es';

export function TalkDownDrawer({ c, onClose }: { c: Ctl; onClose: () => void }) {
  const ev = c.ev;
  const tz = ev.siteTimeZone;
  const d = c.devices;
  useEffect(() => d.reload(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [lang, setLang] = useDraft<Lang>(c.drafts, ev.id, 'td.lang', 'en');
  const [msgId, setMsgId] = useDraft<string | null>(c.drafts, ev.id, 'td.msg', null);
  const [typed, setTyped] = useDraft(c.drafts, ev.id, 'td.text', '');
  const [useTyped, setUseTyped] = useDraft(c.drafts, ev.id, 'td.typed', false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [last, setLast] = useState<TalkdownResult | null>(null);
  const preview = usePreview();
  const now = useNow(1000);

  const sel = new Set(c.speakers);
  const zones = c.speakers.map((id) => d.names.zone(id) ?? id);
  const library = d.data?.library ?? [];
  const msgs = library.filter((m) => m.language === lang && m.active);
  const groups = CATEGORY_ORDER.map((cat) => [cat, msgs.filter((m) => m.category === cat)] as const).concat([['other', msgs.filter((m) => !CATEGORY_ORDER.includes(m.category))]] as const).filter(([, l]) => l.length > 0);
  const msg: TalkdownMessage | null = !useTyped ? (library.find((m) => m.id === msgId) ?? null) : null;
  const text = useTyped ? typed.trim() : msg ? fillMessage(msg.text, ev, zones) : '';
  const language = msg ? (msg.language as Lang) : lang;
  const canPlay = c.canAct && c.speakers.length > 0 && !!text && !busy;
  const maps = ev.sitemaps.filter((m) => m.placements.some((p) => d.byId.get(p.entity_id)?.kind === 'speaker'));
  const marks = useMemo(() => liveMarks(c, now), [c, now]);

  const toggle = (id: string) => c.setSpeakers(sel.has(id) ? c.speakers.filter((x) => x !== id) : [...c.speakers, id]);
  const play = async () => {
    if (!canPlay) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.talkdown(ev.id, useTyped ? { deviceIds: c.speakers, text: typed.trim(), language: lang } : { deviceIds: c.speakers, messageId: msg!.id });
      setLast(r);
      // The drawer shows the result; only a speaker that missed it toasts.
      const failed = r.devices.filter((x) => !x.ok);
      if (failed.length)
        c.toast({
          icon: 'speaker',
          tone: 'warn',
          title: `Talk-down not played on ${failed.length} of ${r.devices.length}`,
          body: `${zones.join(', ')}${r.text ? ` · “${r.text}”` : ''}${r.devices.some((x) => x.simulated) ? ' · simulated speakers' : ''}`,
        });
      c.reload();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  const history = deviceActions(c, 'talk_down');

  return (
    <Drawer
      icon="mic"
      title="Talk-down"
      sub={
        <>
          {ev.site.name}
          {d.data ? ` · voice ${d.data.tts === 'simulated' ? 'simulated (tones until the TTS engine is chosen)' : d.data.tts}` : ''}
        </>
      }
      onClose={onClose}
      onSubmit={() => void play()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn" onClick={() => (preview.state === 'idle' ? void preview.play(text, language) : preview.stop())} disabled={!text} title="Listen here first; nothing plays on site">
            {preview.state === 'loading' ? <span className="spinner" aria-hidden="true" /> : <Icon name={preview.state === 'playing' ? 'pause' : 'speaker'} />}
            {preview.state === 'playing' ? 'Stop preview' : 'Preview'}
          </button>
          <button type="submit" className="btn pri" disabled={!canPlay}>
            {busy ? <span className="spinner" aria-hidden="true" /> : <Icon name="play" />}
            {busy ? 'Playing…' : `Play on ${c.speakers.length ? plural(c.speakers.length, 'speaker') : 'a speaker'}`}
          </button>
        </>
      }
    >
      <DevicesLoading c={c} />
      {d.data && d.speakers.length === 0 && <div className="note warn-t">This site has no speakers in the ERP data.</div>}
      {d.speakers.length > 0 && (
        <div className="sec">
          <div className="lbl">
            <span>Speakers</span>
            <span className="dim">{c.speakers.length ? `${c.speakers.length} chosen` : 'pick at least one'}</span>
          </div>
          <div className="optlist" role="group" aria-label="Speakers">
            {d.speakers.map((s) => {
              const on = sel.has(s.id);
              const playing = deviceActive(s, now);
              return (
                <button key={s.id} type="button" role="checkbox" aria-checked={on} className={`opt${on ? ' on' : ''}`} onClick={() => toggle(s.id)}>
                  <span className="cb" aria-hidden="true">
                    {on && <Icon name="check" />}
                  </span>
                  <span>
                    {s.zone ?? s.name}
                    <small>
                      {s.name}
                      {s.hostCameraId && s.hostCameraId === c.view.cameraId ? ' · on the camera in your player' : ''}
                      {s.method ? ` · ${human(s.method)}` : ''}
                    </small>
                  </span>
                  {playing ? (
                    <span className="devst on spk">
                      <span className="wave sm" aria-hidden="true">
                        {[0, 1, 2].map((i) => (
                          <i key={i} style={{ animationDelay: `${i * 0.1}s` }} />
                        ))}
                      </span>
                      playing
                    </span>
                  ) : (
                    <span className="devst">{s.status !== 'active' ? s.status : ''}</span>
                  )}
                </button>
              );
            })}
          </div>
          {maps[0] && (
            <details className="tdmap">
              <summary>Pick on the site map</summary>
              <div className="mapwrap">
                <SitemapView map={maps[0]} cameras={ev.cameras} devices={[]} compact live={marks} selectedDeviceIds={sel} onDeviceClick={(id) => d.byId.get(id)?.kind === 'speaker' && toggle(id)} selectedCameraId={c.view.cameraId} />
              </div>
            </details>
          )}
        </div>
      )}

      <PttButton c={c} />

      <div className="sec">
        <div className="lbl">
          <span>Messages</span>
          <div className="seg" role="radiogroup" aria-label="Language">
            {(['en', 'es'] as const).map((l) => (
              <button key={l} type="button" role="radio" aria-checked={lang === l} className={lang === l ? 'on' : ''} onClick={() => setLang(l)}>
                {l === 'en' ? 'English' : 'Español'}
              </button>
            ))}
          </div>
        </div>
        {d.data && msgs.length === 0 && <div className="note">No {lang === 'en' ? 'English' : 'Spanish'} messages in the library for this site.</div>}
        {groups.map(([cat, list]) => (
          <div key={cat} className="tdgroup">
            <div className="lbl dim">{categoryLabel(cat)}</div>
            {list.map((m) => {
              const on = !useTyped && msgId === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={`msg${on ? ' on' : ''}`}
                  onClick={() => {
                    setMsgId(m.id);
                    setUseTyped(false);
                  }}
                >
                  <span className="lbl">
                    {m.name}
                    {m.siteId && <span className="tagx">this site</span>}
                  </span>
                  <span>{fillMessage(m.text, ev, zones)}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <label className={`field${useTyped ? ' tdtyped' : ''}`}>
        <span className="lbl">Or type a message ({lang === 'en' ? 'English' : 'Spanish'} voice)</span>
        <textarea
          rows={2}
          maxLength={400}
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            setUseTyped(!!e.target.value.trim());
          }}
          onFocus={() => typed.trim() && setUseTyped(true)}
          placeholder="e.g. You in the red jacket by row C: leave the lot now."
        />
        <span className="note">Spoken exactly as typed. {400 - typed.length} characters left.</span>
      </label>

      {text ? (
        <div className="tdnow" aria-live="polite">
          <span className="lbl">Will play{c.speakers.length ? ` on ${zones.join(', ')}` : ''}</span>
          <span>“{text}”</span>
        </div>
      ) : (
        <div className="note">Pick a message or type one.</div>
      )}
      {preview.err ? <Err error={preview.err} /> : null}
      <Err error={err} />

      {last && (
        <div className="playing" role="status">
          <Icon name="check" />
          <span>
            Played on {zones.join(', ')} · {(last.durationMs / 1000).toFixed(1)} s{last.devices.some((x) => x.simulated) ? ' · simulated' : ''}
          </span>
          <AudioButton url={last.audioUrl} label="Play it back" small />
        </div>
      )}

      {history.length > 0 && (
        <div className="sec">
          <div className="lbl">
            <span>On this event</span>
            <span className="dim">{tzAbbr(tz)} · audio stored as evidence</span>
          </div>
          {history.slice(0, 6).map((a) => (
            <TalkdownRow key={a.id} c={c} a={a} />
          ))}
        </div>
      )}
      <div className="note">Every talk-down is recorded with the event, with its audio.</div>
    </Drawer>
  );
}

/* ---------- deterrents ---------- */

const ARM_MS = 5000;

type Armed = { id: string; action: 'pulse' | 'hold' | 'release'; seconds: number | null; at: number };

function RelayCard({ c, r, armed, setArmed, focus }: { c: Ctl; r: EventDevice; armed: Armed | null; setArmed: (a: Armed | null) => void; focus: boolean }) {
  const now = useNow(250);
  const max = r.hardwareMaxOnS ?? 300;
  const [secs, setSecs] = useDraft(c.drafts, c.ev.id, `deter.s.${r.id}`, Math.min(10, max));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const on = deviceActive(r, now);
  const holding = on && !r.until;
  const human = humanOnly(r, c.devices.data?.humanOnlyFunctions);
  const mine = armed && armed.id === r.id ? armed : null;
  const left = mine ? Math.min(ARM_MS, Math.max(0, ARM_MS - (now - mine.at))) : 0;
  useEffect(() => {
    if (focus) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [focus]);
  useEffect(() => {
    if (mine) confirmRef.current?.focus();
  }, [mine?.at]); // eslint-disable-line react-hooks/exhaustive-deps
  const seconds = Math.max(1, Math.min(max, Math.round(Number(secs) || 10)));
  const fire = async (action: 'pulse' | 'hold' | 'release') => {
    setArmed(null);
    setBusy(true);
    setErr(null);
    try {
      // The device's live state in the drawer shows it.
      await api.relay(c.ev.id, r.id, action === 'pulse' ? { action, seconds } : { action });
      c.reload();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  const arm = (action: 'pulse' | 'hold' | 'release') => setArmed({ id: r.id, action, seconds: action === 'pulse' ? seconds : null, at: Date.now() });
  const what = (a: Armed) => (a.action === 'pulse' ? `pulse ${a.seconds} s` : a.action === 'hold' ? (human ? `open / switch on (hold)` : 'hold on') : 'release');
  return (
    <div ref={ref} className={`relay${on ? ' on' : ''}${human ? ' human' : ''}${focus ? ' focus' : ''}`}>
      <div className="rhead">
        <span className={`rfn f-${r.function ?? 'other'}`}>
          <Icon name={functionIcon(r.function)} />
        </span>
        <div>
          <b>{r.name}</b>
          <div className="note">
            {functionLabel(r.function)}
            {r.zone ? ` · ${r.zone}` : ''}
            {r.method ? ` · ${r.method.replace(/_/g, ' ')}` : ''}
            {r.hardwareMaxOnS ? ` · max ${r.hardwareMaxOnS} s` : ''}
          </div>
          {human && (
            <div className="humanonly">
              <Icon name="lock" />
              {r.function === 'gate' ? 'Gate' : 'Lock'} · human decision only. Confirm you mean to operate it.
            </div>
          )}
        </div>
      </div>
      <span className={`st${on ? ' on' : ''}`} aria-live="polite">
        {on ? (
          holding ? (
            'HOLDING'
          ) : (
            <>
              ON · <Left until={r.until} />
            </>
          )
        ) : r.state === 'unknown' ? (
          'UNKNOWN'
        ) : (
          'OFF'
        )}
      </span>
      <div className="bts">
        {mine ? (
          <>
            <button ref={confirmRef} type="button" className={`btn arm${human ? ' danger' : ''}`} onClick={() => void fire(mine.action)} disabled={busy}>
              <Icon name="check" />
              Confirm {what(mine)}
            </button>
            <button type="button" className="btn" onClick={() => setArmed(null)}>
              Cancel
            </button>
            <span className="mono dim">{Math.ceil(left / 1000)} s</span>
          </>
        ) : on ? (
          <button type="button" className="btn" onClick={() => (human ? arm('release') : void fire('release'))} disabled={busy || !c.canAct}>
            {busy ? <span className="spinner" aria-hidden="true" /> : <Icon name="x" />}
            Release
          </button>
        ) : (
          <>
            <span className="secs">
              <input
                type="number"
                min={1}
                max={max}
                value={secs}
                onChange={(e) => setSecs(Number(e.target.value))}
                onBlur={() => setSecs(seconds)}
                aria-label={`Pulse seconds for ${r.name} (1 to ${max})`}
                className="compact"
              />
              <span className="muted">s</span>
            </span>
            <button type="button" className="btn" onClick={() => arm('pulse')} disabled={busy || !c.canAct}>
              <Icon name="bolt" />
              Pulse {seconds} s
            </button>
            <button type="button" className="btn" onClick={() => arm('hold')} disabled={busy || !c.canAct} title="Stays on until you release it">
              Hold on
            </button>
          </>
        )}
      </div>
      <Err error={err} />
    </div>
  );
}

export function DeterrentsDrawer({ c, relayId, onClose }: { c: Ctl; relayId?: string; onClose: () => void }) {
  const d = c.devices;
  useEffect(() => d.reload(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [armed, setArmed] = useState<Armed | null>(null);
  const now = useNow(500);
  // An armed button disarms itself after ARM_MS (two-step confirm, blueprint §6).
  useEffect(() => {
    if (armed && now - armed.at > ARM_MS) setArmed(null);
  }, [armed, now]);
  const history = deviceActions(c, 'relay');
  const anyOn = d.relays.some((r) => deviceActive(r, now));
  return (
    <Drawer
      icon="bolt"
      title="Deterrent devices"
      sub={`Relays on ${c.ev.site.name}${anyOn ? ' · active now' : ''}`}
      onClose={onClose}
      onSubmit={() => undefined}
      footer={
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      <DevicesLoading c={c} />
      {d.data && d.relays.length === 0 && <div className="note warn-t">This site has no relays in the ERP data.</div>}
      {d.relays.map((r) => (
        <RelayCard key={r.id} c={c} r={r} armed={armed} setArmed={setArmed} focus={r.id === relayId} />
      ))}
      <div className="note">Two-step confirm prevents misfires: press, then confirm within {ARM_MS / 1000} s. A pulse never runs past the relay's hardware maximum. Every trigger is logged and audited.</div>
      {history.length > 0 && (
        <div className="sec">
          <div className="lbl">
            <span>On this event</span>
            <span className="dim">{tzAbbr(c.ev.siteTimeZone)}</span>
          </div>
          {history.slice(0, 8).map((a) => (
            <RelayRow key={a.id} c={c} a={a} />
          ))}
        </div>
      )}
    </Drawer>
  );
}

/* ---------- notify the customer (D11) ---------- */

/** Template codes the agent tool knows (api-phase5.md); used when the template list is not readable (operators). */
const KNOWN_CODES: { code: string; name: string }[] = [
  { code: 'activity', name: 'Activity: we are on it' },
  { code: 'police', name: 'Police dispatched' },
  { code: 'all_clear', name: 'All clear' },
];

const STATUS_TONE: Record<string, string> = { sent: 'ok', simulated: 'warn', failed: 'bad', queued: '' };

function fillTemplate(t: string, v: Record<string, string>): string {
  return t.replace(/\{(\w+)\}/g, (_m, k: string) => v[k] ?? '');
}

/** What was sent for the event (GET /api/events/:id/notifications), refetched on the event's SSE messages. */
export function NotificationList({ c, compact }: { c: Ctl; compact?: boolean }) {
  const load = useLoad((sig) => api.eventNotifications(c.ev.id, sig), [c.ev.id]);
  useRefreshOn(['event'], load.reload, 800);
  const tz = c.ev.siteTimeZone;
  const list = load.data ?? [];
  if (load.error && !load.data) return <div className="note bad-t">Could not load notifications: {errorText(load.error)}</div>;
  if (!load.data) return <div className="note">Loading notifications…</div>;
  if (!list.length) return <div className="note">Nothing sent to the customer for this event.</div>;
  return (
    <div className="nlist">
      {list
        .slice()
        .reverse()
        .slice(0, compact ? 5 : 20)
        .map((n: EventNotification) => (
          <details key={n.id} className="nrow">
            <summary>
              <Icon name={n.channel === 'email' ? 'mail' : 'phone'} />
              <span className="mono dim">{fmtTime(n.createdAt, tz)}</span>
              <span className="nwho">{n.contactName ?? n.contactId}</span>
              <span className="dim">
                {n.purpose === 'customer_report' ? 'Customer report' : human(n.templateCode)} · {n.channel === 'email' ? 'email' : 'SMS'}
                {n.actorType === 'agent' ? ' · by AI' : n.actorType === 'system' ? ' · system' : ''}
              </span>
              <span className={`pill tiny-pill ${STATUS_TONE[n.status] ?? ''}`} title={n.status === 'simulated' ? 'Recorded, not sent: SMS / email is not configured on this server' : n.error ?? undefined}>
                {n.status}
              </span>
            </summary>
            {n.subject && <div className="small strong">{n.subject}</div>}
            <div className="small pre">{n.body}</div>
            {n.error && <div className="note bad-t">{n.error}</div>}
            {n.provider && <div className="note">Provider: {n.provider}</div>}
          </details>
        ))}
    </div>
  );
}

export function NotifyDrawer({ c, onClose }: { c: Ctl; onClose: () => void }) {
  const ev = c.ev;
  const s = useSession();
  const isSup = s.has('supervisor', 'admin');
  const templates = useLoad<NotificationTemplate[] | null>((sig) => api.notificationTemplates(sig).catch(() => null), []);
  const recips = useLoad((sig) => (isSup ? api.notificationRecipients(ev.siteId, sig) : Promise.resolve(null)), [ev.siteId, isSup]);
  const [code, setCode] = useDraft(c.drafts, ev.id, 'notify.code', 'activity');
  const aiSummary = c.agent.data && c.agent.data.visible ? (c.agent.data.summary ?? '') : '';
  const [summary, setSummary] = useDraft(c.drafts, ev.id, 'notify.summary', '');
  const [pick, setPick] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [result, setResult] = useState<{ id: string; status: string; to: string }[] | null>(null);
  const tz = ev.siteTimeZone;
  const tlist = (templates.data ?? []).filter((t) => t.purpose === 'event_notice' && t.active);
  const codes = tlist.length ? [...new Map(tlist.map((t) => [t.code, { code: t.code, name: t.name }])).values()] : KNOWN_CODES;
  const forCode = tlist.filter((t) => t.code === code);
  const recList = (recips.data ?? []).filter((r) => r.active && r.purposes.includes('event_notice'));
  const chosen = pick ?? new Set(recList.map((r) => r.id));
  const vars: Record<string, string> = {
    site: ev.site.name,
    time: new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(ev.firstAlarmAt)),
    summary: (summary.trim() || ev.disposition?.notes || aiSummary || '').slice(0, 300),
    event: ev.displayNo,
    report_url: `${window.location.origin}/#/events/${ev.id}`,
  };
  const send = async () => {
    if (!code || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const ids = isSup && recips.data && pick ? [...chosen] : undefined;
      const r = await api.notifyCustomer(ev.id, { templateCode: code, ...(ids && ids.length ? { recipientIds: ids } : {}), ...(summary.trim() ? { summary: summary.trim() } : {}) });
      setResult(r.notifications);
      // The drawer lists each message's status; a toast only when none went out.
      if (!r.notifications.some((n) => n.status !== 'failed'))
        c.toast({ icon: 'send', tone: 'bad', title: 'Nobody was reached', body: r.notifications.map((n) => `${n.to}: ${n.status}`).join(' · ') });
      c.drafts.clear(ev.id, 'notify.');
      c.reload();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer
      icon="send"
      title="Notify the customer"
      sub={`${ev.site.name} · texts and emails go to the site's notification contacts`}
      onClose={onClose}
      onSubmit={() => void send()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
          <button type="submit" className="btn pri" disabled={!code || busy || (isSup && !!recips.data && chosen.size === 0)}>
            {busy ? <span className="spinner" aria-hidden="true" /> : <Icon name="send" />}
            {busy ? 'Sending…' : 'Send'}
          </button>
        </>
      }
    >
      <div className="sec">
        <div className="lbl">Message</div>
        <div className="optlist" role="radiogroup" aria-label="Template">
          {codes.map((t) => (
            <button key={t.code} type="button" role="radio" aria-checked={code === t.code} className={`opt${code === t.code ? ' on' : ''}`} onClick={() => setCode(t.code)}>
              <span className="rd" aria-hidden="true" />
              <span>{t.name}</span>
              <span className="mono dim tiny">{t.code}</span>
            </button>
          ))}
        </div>
      </div>
      <label className="field">
        <span className="lbl">Summary for the customer · one plain sentence</span>
        <input value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={300} placeholder={aiSummary ? `Empty: the AI summary (“${aiSummary.slice(0, 60)}${aiSummary.length > 60 ? '…' : ''}”)` : 'e.g. Two people at the east fence left after a warning.'} />
      </label>
      <div className="sec">
        <div className="lbl">
          <span>Preview</span>
          <span className="dim">{forCode.length ? forCode.map((t) => (t.channel === 'email' ? 'email' : 'SMS')).join(' · ') : ''}</span>
        </div>
        {templates.data === undefined ? (
          <div className="note">Loading templates…</div>
        ) : templates.data === null ? (
          <div className="note">The rendered preview needs the template list, which your role cannot read yet. The server fills the “{code}” template with the site, time, summary and event number.</div>
        ) : forCode.length === 0 ? (
          <div className="note warn-t">No active “{code}” template. An admin adds one in Setup → site → Notifications.</div>
        ) : (
          forCode.map((t) => (
            <div key={`${t.code}-${t.channel}-${t.language}`} className="npreview">
              <span className="lbl">
                <Icon name={t.channel === 'email' ? 'mail' : 'phone'} /> {t.channel === 'email' ? 'Email' : 'SMS'}
                {t.language !== 'en' ? ` · ${t.language}` : ''}
              </span>
              {t.subject && <b className="small">{fillTemplate(t.subject, vars)}</b>}
              <span className="small pre">{fillTemplate(t.body, vars).slice(0, t.channel === 'sms' ? 480 : 8000)}</span>
            </div>
          ))
        )}
      </div>
      <div className="sec">
        <div className="lbl">Recipients</div>
        {!isSup ? (
          <div className="note">Goes to every contact set up for event notices at this site (Setup → site → Notifications).</div>
        ) : recips.error ? (
          <Err error={recips.error} />
        ) : !recips.data ? (
          <div className="note">Loading recipients…</div>
        ) : recList.length === 0 ? (
          <div className="note warn-t">No event-notice recipients at this site. An admin adds them in Setup → site → Notifications.</div>
        ) : (
          <div className="optlist" role="group" aria-label="Recipients">
            {recList.map((r) => {
              const on = chosen.has(r.id);
              return (
                <button
                  key={r.id}
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  className={`opt${on ? ' on' : ''}`}
                  onClick={() => {
                    const n = new Set(chosen);
                    if (on) n.delete(r.id);
                    else n.add(r.id);
                    setPick(n);
                  }}
                >
                  <span className="cb" aria-hidden="true">
                    {on && <Icon name="check" />}
                  </span>
                  <span>{r.contactName ?? r.contactId}</span>
                  <span className="mono dim tiny">{r.channel === 'email' ? 'email' : 'SMS'}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <Err error={err} />
      {result && (
        <div className="playing" role="status">
          <Icon name="check" />
          <span>{result.map((n) => `${n.to}: ${n.status}`).join(' · ')}</span>
        </div>
      )}
      <div className="sec">
        <div className="lbl">Sent for this event</div>
        <NotificationList c={c} />
      </div>
      <div className="note">Numbers and addresses come from the ERP contact at send time. Without SMS / email configured on the server, messages are recorded as “simulated” and nothing leaves the SOC.</div>
    </Drawer>
  );
}

/**
 * The customer report's delivery for a closed event (customer_report_status: generated → sent / failed). With no
 * report recipients the status stays 'generated' and the log says it was kept with the event, so read the log too.
 */
export function ReportStatus({ status, log = [] }: { status: string | null | undefined; log?: { kind: string; text: string }[] }): ReactNode {
  if (!status || status === 'none' || status === 'not_required' || status === 'not_requested') return null;
  const kept = status === 'generated' ? log.find((l) => l.kind === 'customer_report_not_sent') : undefined;
  const tone = status === 'sent' ? 'ok' : status === 'failed' ? 'bad' : kept ? 'warn' : 'info';
  const word = kept ? 'kept with the event (no recipients)' : status === 'generated' ? 'generated, sending' : status;
  return (
    <span className={`pill tiny-pill ${tone}`} title={kept?.text ?? 'Customer report delivery'}>
      customer report {word}
    </span>
  );
}
